import { Component } from '@theme/component';

/**
 * Live countdown timer for the PWCT countdown block.
 *
 * @typedef {object} PwctCountdownTimerRefs
 * @property {HTMLElement} [daysValue]
 * @property {HTMLElement} [hoursValue]
 * @property {HTMLElement} [minutesValue]
 * @property {HTMLElement} [secondsValue]
 * @property {HTMLElement} [daysUnit]
 * @property {HTMLElement} [daysSeparator]
 * @property {HTMLElement} [completeMessage]
 * @property {HTMLElement} [beforeStartMessage]
 * @property {HTMLElement} [timer]
 * @property {HTMLElement} [heading]
 *
 * @extends {Component<PwctCountdownTimerRefs>}
 */
class PwctCountdownTimer extends Component {
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;

  /** @type {number} */
  #endAt = 0;

  /** @type {number} */
  #startAt = 0;

  /** @type {boolean} */
  #completed = false;

  connectedCallback() {
    super.connectedCallback();

    this.#endAt = Number(this.dataset.endAt || 0) * 1000;
    this.#startAt = Number(this.dataset.startAt || 0) * 1000;
    this.#completed = false;

    this.#tick();
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearInterval();
  }

  #clearInterval() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  get #isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  #tick() {
    if (!this.#endAt) return;

    const now = Date.now();

    if (this.#startAt > 0 && now < this.#startAt) {
      this.#handleBeforeStart(now);
      return;
    }

    const remaining = Math.max(0, this.#endAt - now);

    if (remaining <= 0) {
      this.#render(0);
      if (!this.#completed) this.#handleComplete();
      return;
    }

    this.#showActiveTimer();
    this.#render(remaining);
  }

  /**
   * @param {number} remainingMs
   */
  #render(remainingMs) {
    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#setValue(this.refs.daysValue, days);
    this.#setValue(this.refs.hoursValue, hours);
    this.#setValue(this.refs.minutesValue, minutes);
    this.#setValue(this.refs.secondsValue, seconds);

    const hideZeroDays = this.dataset.hideZeroDays === 'true';
    const hasOtherUnits = Boolean(
      this.refs.hoursValue || this.refs.minutesValue || this.refs.secondsValue
    );

    if (this.refs.daysUnit instanceof HTMLElement && hideZeroDays && hasOtherUnits) {
      const hideDays = days === 0;
      this.refs.daysUnit.hidden = hideDays;
      if (this.refs.daysSeparator instanceof HTMLElement) {
        this.refs.daysSeparator.hidden = hideDays;
      }
    }
  }

  /**
   * @param {Element | Element[] | undefined} el
   * @param {number} value
   */
  #setValue(el, value) {
    if (!(el instanceof HTMLElement)) return;

    const next = this.#format(value);
    const textEl = el.querySelector('.pwct-countdown-timer__value-text');
    const target = textEl instanceof HTMLElement ? textEl : el;

    if (target.textContent === next) return;

    const sizerEl = el.querySelector('.pwct-countdown-timer__value-sizer');
    if (sizerEl instanceof HTMLElement) {
      const sizerText = sizerEl.textContent ?? '';
      if (next.length > sizerText.length) {
        sizerEl.textContent = '8'.repeat(next.length);
      }
    }

    target.textContent = next;

    if (this.dataset.tickAnimation === 'true') {
      el.classList.remove('is-ticking');
      void el.offsetWidth;
      el.classList.add('is-ticking');
    }
  }

  /**
   * @param {number} value
   * @returns {string}
   */
  #format(value) {
    const pad = this.dataset.padZeros !== 'false';
    return pad ? String(value).padStart(2, '0') : String(value);
  }

  #showActiveTimer() {
    this.hidden = false;

    if (this.refs.timer instanceof HTMLElement) {
      this.refs.timer.hidden = false;
    }

    if (this.refs.heading instanceof HTMLElement) {
      this.refs.heading.hidden = false;
    }

    if (this.refs.completeMessage instanceof HTMLElement) {
      this.refs.completeMessage.hidden = true;
    }

    if (this.refs.beforeStartMessage instanceof HTMLElement) {
      this.refs.beforeStartMessage.hidden = true;
    }
  }

  /**
   * @param {number} now
   */
  #handleBeforeStart(now) {
    const action = this.dataset.beforeStartAction || 'hide';

    if (action === 'hide' && !this.#isDesignMode) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    if (action === 'message') {
      if (this.refs.timer instanceof HTMLElement) this.refs.timer.hidden = true;
      if (this.refs.completeMessage instanceof HTMLElement) this.refs.completeMessage.hidden = true;
      if (this.refs.beforeStartMessage instanceof HTMLElement) {
        this.refs.beforeStartMessage.hidden = false;
      }
      return;
    }

    this.#showActiveTimer();
    this.#render(Math.max(0, this.#endAt - now));
  }

  #handleComplete() {
    this.#completed = true;
    this.#clearInterval();

    const action = this.dataset.completeAction || 'message';

    if (action === 'hide' && !this.#isDesignMode) {
      this.hidden = true;
      return;
    }

    if (action === 'message') {
      if (this.refs.timer instanceof HTMLElement) this.refs.timer.hidden = true;

      if (this.dataset.hideHeadingOnComplete === 'true' && this.refs.heading instanceof HTMLElement) {
        this.refs.heading.hidden = true;
      }

      if (this.refs.completeMessage instanceof HTMLElement) {
        this.refs.completeMessage.hidden = false;
      } else if (!this.#isDesignMode) {
        this.hidden = true;
      }
    }
  }
}

if (!customElements.get('pwct-countdown-timer')) {
  customElements.define('pwct-countdown-timer', PwctCountdownTimer);
}
